/**
 * DTF Town — Cart drawer extras.
 *
 * Adds three behaviours to the cart drawer:
 *   1. <coin-earn-estimate>  — fetches the coin reward for the current cart
 *      subtotal from the dashboard (App Proxy) and shows "you'll earn X
 *      coins". The number is computed server-side so it matches the real
 *      earning logic; the client never derives coins from dollars.
 *   2. Quick-add upsell       — in-drawer add-to-cart with an optional
 *      color/size picker for multi-variant products.
 *   3. Estimate-shipping toggle.
 *
 * Upsell + toggle use event delegation on document so they keep working
 * after the cart-drawer section is re-rendered (which replaces the DOM).
 */
(function () {
  'use strict';

  const COIN_API = '/apps/coin-rewards';

  function money(cents) {
    if (typeof formatMoney === 'function' && window.theme && window.theme.settings) {
      return formatMoney(cents, window.theme.settings.money_format || '${{amount}}');
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  /* ---- 1. Coin earn estimate -------------------------------------------- */

  if (!customElements.get('coin-earn-estimate')) {
    class CoinEarnEstimate extends HTMLElement {
      connectedCallback() {
        this.lead = this.querySelector('[data-coin-lead]');
        const cents = parseInt(this.dataset.subtotal, 10) || 0;
        this.signedIn = this.dataset.signedIn === 'true';
        this.load(cents);
      }

      async load(cents) {
        try {
          const res = await fetch(`${COIN_API}/estimate?subtotal_cents=${cents}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!res.ok) throw new Error('estimate failed');
          const data = await res.json();
          this.render(data);
        } catch (e) {
          // On failure, hide the line rather than show a wrong number.
          this.style.display = 'none';
        }
      }

      render(data) {
        const coins = parseInt(data.coins, 10) || 0;
        if (coins <= 0) {
          this.style.display = 'none';
          return;
        }
        const unit = coins === 1 ? 'coin' : 'coins';
        const strong = `<strong data-coin-amount>${coins.toLocaleString('en-US')}</strong>`;
        if (this.lead) {
          this.lead.innerHTML = data.signed_in
            ? `You'll earn ${strong} ${unit} with this order`
            : `Earn ${strong} ${unit} on this order — sign in to collect them`;
        }
        this.style.display = '';
      }
    }
    customElements.define('coin-earn-estimate', CoinEarnEstimate);
  }

  /* ---- 2. Quick-add upsell ---------------------------------------------- */

  // Resolve the selected variant for a multi-variant upsell card from its
  // option <select>s and the embedded variants JSON, then sync the Add
  // button's variant id, the price and availability.
  function syncUpsellVariant(item) {
    const script = item.querySelector('[data-upsell-variants]');
    const addBtn = item.querySelector('[data-upsell-add]');
    if (!script || !addBtn) return;

    let variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }

    const selects = item.querySelectorAll('.cart-upsell__select');
    const chosen = [];
    selects.forEach((sel) => {
      chosen[parseInt(sel.dataset.optionPosition, 10) - 1] = sel.value;
    });

    const match = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
    const priceEl = item.querySelector('[data-upsell-price]');

    if (match) {
      addBtn.dataset.variantId = match.id;
      addBtn.disabled = !match.available;
      addBtn.textContent = match.available ? '+ Add' : 'Sold out';
      if (priceEl) priceEl.textContent = money(match.price);
    } else {
      addBtn.disabled = true;
      addBtn.textContent = 'Unavailable';
    }
  }

  async function addUpsell(addBtn) {
    const variantId = addBtn.dataset.variantId;
    if (!variantId || addBtn.disabled) return;

    addBtn.classList.add('loading');
    addBtn.disabled = true;

    try {
      const form = new FormData();
      form.append('id', variantId);
      form.append('quantity', 1);
      form.append('sections', 'cart-drawer,cart-bubble,main-cart');
      form.append('sections_url', window.location.pathname);

      const routes = (window.theme && window.theme.routes) || {};
      const res = await fetch(routes.cart_add_url || '/cart/add', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: form,
      });
      const data = await res.json();
      if (data && data.status) {
        addBtn.classList.remove('loading');
        addBtn.disabled = false;
        return;
      }
      // Let the cart drawer re-render itself from the server.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      addBtn.classList.remove('loading');
      addBtn.disabled = false;
      addBtn.textContent = 'Added';
      window.setTimeout(function () {
        if (addBtn.isConnected) addBtn.textContent = '+ Add';
      }, 1200);
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (e) {
      addBtn.classList.remove('loading');
      addBtn.disabled = false;
    }
  }

  /* ---- 3. Estimate-shipping toggle -------------------------------------- */

  function toggleShipping(btn) {
    const body = document.getElementById(btn.getAttribute('aria-controls'));
    if (!body) return;
    const open = body.hasAttribute('hidden');
    if (open) {
      body.removeAttribute('hidden');
    } else {
      body.setAttribute('hidden', '');
    }
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* ---- Delegated listeners (survive re-render) -------------------------- */

  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('.cart-upsell__select');
    if (sel) {
      const item = sel.closest('[data-upsell-item]');
      if (item) syncUpsellVariant(item);
    }
  });

  document.addEventListener('click', (e) => {
    const addBtn = e.target.closest && e.target.closest('[data-upsell-add]');
    if (addBtn) {
      e.preventDefault();
      addUpsell(addBtn);
      return;
    }
    const shipToggle = e.target.closest && e.target.closest('[data-shipping-estimate-toggle]');
    if (shipToggle) {
      e.preventDefault();
      toggleShipping(shipToggle);
    }
  });
})();
